// Network layer for the snippet. Two surfaces:
//   1. fetchConfig(url, etag) → public config endpoint with conditional GET.
//   2. postConsent(url, body) → consent endpoint with keepalive + retry queue.

import type { Config, PendingEvent } from './types';
import { PENDING_KEY } from './storage';

interface ApiCategoryDef {
  id: 'strictly_necessary' | 'functional' | 'analytics' | 'marketing';
  label: string;
  description?: string;
  required?: boolean;
}

interface ApiConfigResponse {
  publicId: string;
  policyRevision: number;
  bannerTextHash: string;
  language: 'en' | 'ga';
  categories: ApiCategoryDef[];
  copy: Config['copy'];
  theme: Config['theme'];
  defaults: Config['defaults'];
  currentSnippetVersion: string;
}

const ETAG_KEY = 'cookiesie_cfg_etag';
const CFG_KEY = 'cookiesie_cfg';

export async function fetchConfig(url: string): Promise<Config | null> {
  let cachedEtag: string | null = null;
  let cachedConfig: Config | null = null;
  try {
    cachedEtag = window.localStorage.getItem(ETAG_KEY);
    const raw = window.localStorage.getItem(CFG_KEY);
    if (raw) cachedConfig = JSON.parse(raw) as Config;
  } catch {}

  let response: Response;
  try {
    response = await fetch(url, {
      method: 'GET',
      headers: cachedEtag ? { 'if-none-match': cachedEtag } : undefined,
      credentials: 'omit',
      cache: 'default',
    });
  } catch {
    return cachedConfig;
  }

  if (response.status === 304 && cachedConfig) return cachedConfig;
  if (!response.ok) return cachedConfig;

  let parsed: ApiConfigResponse;
  try {
    parsed = (await response.json()) as ApiConfigResponse;
  } catch {
    return cachedConfig;
  }

  const config: Config = {
    categories: parsed.categories,
    copy: parsed.copy,
    theme: parsed.theme,
    defaults: parsed.defaults,
    policyRevision: parsed.policyRevision,
    language: parsed.language,
    bannerTextHash: parsed.bannerTextHash,
  };

  try {
    const etag = response.headers.get('etag');
    if (etag) window.localStorage.setItem(ETAG_KEY, etag);
    window.localStorage.setItem(CFG_KEY, JSON.stringify(config));
  } catch {}

  return config;
}

export async function postConsent(url: string, evt: PendingEvent): Promise<boolean> {
  try {
    const res = await fetch(url, {
      method: 'POST',
      keepalive: true,
      headers: { 'content-type': 'application/json' },
      credentials: 'omit',
      body: JSON.stringify({
        projectId: evt.projectId,
        choice: evt.choice,
        categories: evt.categories,
        consentString: evt.consentString,
        tcfConsentString: null,
        uiVersion: evt.uiVersion,
        snippetVersion: evt.snippetVersion,
        language: evt.language,
        policyRevision: evt.policyRevision,
        bannerTextHash: evt.bannerTextHash,
        pageUrl: evt.pageUrl,
      }),
    });
    return res.ok || res.status === 204;
  } catch {
    return false;
  }
}

export function loadPendingQueue(): PendingEvent[] {
  try {
    const raw = window.localStorage.getItem(PENDING_KEY);
    return raw ? (JSON.parse(raw) as PendingEvent[]) : [];
  } catch {
    return [];
  }
}

export function savePendingQueue(arr: PendingEvent[]): void {
  try {
    while (arr.length > 5) arr.shift();
    window.localStorage.setItem(PENDING_KEY, JSON.stringify(arr));
  } catch {}
}

export function clearPendingQueue(): void {
  try {
    window.localStorage.removeItem(PENDING_KEY);
  } catch {}
}
